import React, { useMemo } from "react";
import { ErrorIcon, ConfirmationIcon, InfoIcon, CloseIcon } from "./Icons";
import { useTranslation } from "react-i18next";

const Modal = ({
  title,
  message,
  onConfirm,
  onClose,
  type = "info", // error, confirmation, info
}) => {
  const { t } = useTranslation();

  const icon = useMemo(() => {
    if (type === "error") {
      return <ErrorIcon />;
    }
    if (type === "confirmation") {
      return <ConfirmationIcon />;
    }
    return <InfoIcon />;
  }, [type]);

  const buttonStyle = useMemo(() => {
    if (type === "confirmation") {
      return "bg-green-600 hover:bg-green-700 focus:ring-green-500";
    }
    return "bg-primary hover:bg-primary-hover focus:ring-primary-hover";
  }, [type]);

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      data-testid="modal-container"
    >
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      ></div>
      <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all sm:max-w-lg sm:w-full m-4">
        {/* Header with close button */}
        <div className="flex justify-end p-2">
          <button
            onClick={onClose}
            className="text-black-light hover:text-black-light focus:outline-none"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Content */}
        <div className="px-4 pb-4">
          <div className="sm:flex sm:items-center">
            {icon}
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
              {title && (
                <h3 className="text-lg leading-6 font-medium text-black mb-2">
                  {title}
                </h3>
              )}
              <div className="mt-2">
                <p
                  className="text-base text-black [&_a]:text-blue-600 [&_a]:underline"
                  dangerouslySetInnerHTML={{ __html: message }}
                ></p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer with action buttons */}
        <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          {type === "info" ? (
            <>
              <button
                type="button"
                onClick={onConfirm}
                className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm ${buttonStyle}`}
              >
                {t("general.confirm")}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-black hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                {t("general.cancel")}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:w-auto sm:text-sm ${buttonStyle}`}
            >
              {t("general.ok")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Modal;
